// client/src/pages/admin/AdminE-Catalogue.jsx

import React, { useState, useEffect } from 'react';
import api from '../../utils/axios';
import Swal from 'sweetalert2';

const API_BASE = process.env.REACT_APP_BASE_URL || "http://localhost:5000/";

const AdminECatalogue = () => {
  // ─── State ──────────────────────────────────────────────────────────────────
  const [catalogues, setCatalogues] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modal controls:
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentCatalogue, setCurrentCatalogue] = useState(null);

  // Form data:
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image: null,
    imagePreview: '',
    pdfFile: null,
    pdfPreview: '',
    isActive: true
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // ─── On Mount: fetch catalogues ──────────────────────────────────────────────
  useEffect(() => {
    fetchCatalogues();
  }, []);

  // ─── Fetch All Catalogues ───────────────────────────────────────────────────
  const fetchCatalogues = async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get('/e-catalogues');
      setCatalogues(data);
    } catch (err) {
      console.error('Error loading catalogues:', err);
      Swal.fire('Error', 'Failed to load catalogues', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Open "Add New Catalogue" Modal ───────────────────────────────────────────
  const openAddModal = () => {
    setEditMode(false);
    setCurrentCatalogue(null);
    setFormData({
      name: '',
      description: '',
      image: null,
      imagePreview: '',
      pdfFile: null,
      pdfPreview: '',
      isActive: true
    });
    setIsModalOpen(true);
  };

  // ─── Open "Edit Catalogue" Modal ──────────────────────────────────────────────
  const openEditModal = (catalogue) => {
    setEditMode(true);
    setCurrentCatalogue(catalogue);

    // Pre‐fill the form with existing data
    const imagePreviewUrl = catalogue.image_path
      ? catalogue.image_path.startsWith('http')
        ? catalogue.image_path
        : `${API_BASE}catalogue.image_path`
      : '';

    const pdfPreviewUrl = catalogue.pdf_path
      ? catalogue.pdf_path.startsWith('http')
        ? catalogue.pdf_path
        : `${API_BASE}catalogue.pdf_path`
      : '';

    setFormData({
      name: catalogue.name || '',
      description: catalogue.description || '',
      image: null,
      imagePreview: imagePreviewUrl,
      pdfFile: null,
      pdfPreview: pdfPreviewUrl,
      isActive: catalogue.isActive !== false
    });
    setIsModalOpen(true);
  };

  // ─── Close Modal & Reset ─────────────────────────────────────────────────────
  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentCatalogue(null);
    setFormData({
      name: '',
      description: '',
      image: null,
      imagePreview: '',
      pdfFile: null,
      pdfPreview: '',
      isActive: true
    });
  };

  // ─── Handle Form Changes ─────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(fd => ({
      ...fd,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // ─── Handle Image Selection ───────────────────────────────────────────────────
  const handleImageChange = (e) => {
    if (!e.target.files?.[0]) return;
    const file = e.target.files[0];
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      Swal.fire('Error', 'Please select an image file', 'error');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire('Error', 'Image size should be less than 5MB', 'error');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setFormData(fd => ({
        ...fd,
        image: file,
        imagePreview: e.target.result
      }));
    };
    reader.readAsDataURL(file);
  };

  // ─── Handle PDF Selection ───────────────────────────────────────────────────
  const handlePdfChange = (e) => {
    if (!e.target.files?.[0]) return;
    const file = e.target.files[0];
    
    // Validate file type
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      Swal.fire('Error', 'Please select a PDF file', 'error');
      return;
    }

    // Validate file size (max 100MB)
    if (file.size > 100 * 1024 * 1024) {
      Swal.fire('Error', 'PDF size should be less than 100MB', 'error');
      return;
    }

    setFormData(fd => ({
      ...fd,
      pdfFile: file,
      pdfPreview: file.name
    }));
  };

  // ─── Upload Single File ─────────────────────────────────────────────────────
  const uploadSingleFile = async (file, type) => {
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await api.post('/upload/file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return data.path;
  };

  // ─── Handle Form Submit ─────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      Swal.fire('Error', 'Name is required', 'error');
      return;
    }

    if (!editMode && !formData.pdfFile) {
      Swal.fire('Error', 'PDF file is required for new catalogues', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      let imagePath = '';
      let pdfPath = '';

      // Upload image if selected
      if (formData.image) {
        imagePath = await uploadSingleFile(formData.image, 'image');
      }

      // Upload PDF if selected
      if (formData.pdfFile) {
        pdfPath = await uploadSingleFile(formData.pdfFile, 'pdf');
      }

      const catalogueData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        isActive: formData.isActive
      };

      if (imagePath) catalogueData.image_path = imagePath;
      if (pdfPath) catalogueData.pdf_path = pdfPath;

      if (editMode) {
        // Update existing catalogue
        await api.put(`/e-catalogues/${currentCatalogue.id}`, catalogueData);
        Swal.fire('Success', 'Catalogue updated successfully', 'success');
      } else {
        // Create new catalogue
        await api.post('/e-catalogues', catalogueData);
        Swal.fire('Success', 'Catalogue created successfully', 'success');
      }

      closeModal();
      fetchCatalogues();
    } catch (err) {
      console.error('Error saving catalogue:', err);
      Swal.fire('Error', err.response?.data?.message || 'Failed to save catalogue', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Handle Delete ───────────────────────────────────────────────────────────
  const handleDelete = (catalogue) => {
    Swal.fire({
      title: 'Delete Catalogue',
      text: `Are you sure you want to delete "${catalogue.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`/e-catalogues/${catalogue.id}`);
          Swal.fire('Deleted!', 'Catalogue has been deleted.', 'success');
          fetchCatalogues();
        } catch (err) {
          console.error('Error deleting catalogue:', err);
          Swal.fire('Error', 'Failed to delete catalogue', 'error');
        }
      }
    });
  };

  // ─── Handle Download ─────────────────────────────────────────────────────────
  const handleDownload = (catalogue) => {
    const pdfUrl = catalogue.pdf_path?.startsWith('http')
      ? catalogue.pdf_path
      : `${API_BASE}catalogue.pdf_path`;
    
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `${catalogue.name}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ─── Toggle Active Status ───────────────────────────────────────────────────
  const toggleActive = async (catalogue) => {
    try {
      await api.patch(`/e-catalogues/${catalogue.id}`, {
        isActive: !catalogue.isActive
      });
      fetchCatalogues();
    } catch (err) {
      console.error('Error toggling status:', err);
      Swal.fire('Error', 'Failed to update status', 'error');
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">E-Catalogue Management</h1>
        <button
          onClick={openAddModal}
          className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
        >
          Add New Catalogue
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Catalogue
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {catalogues.length > 0 ? (
                  catalogues.map((catalogue) => (
                    <tr key={catalogue.id} className="hover:bg-gray-50">
                      {/* Catalogue Info */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-12 w-12">
                            {catalogue.image_path ? (
                              <img
                                className="h-12 w-12 rounded-lg object-cover"
                                src={
                                  catalogue.image_path.startsWith('http')
                                    ? catalogue.image_path
                                    : `${API_BASE}catalogue.image_path`
                                }
                                alt={catalogue.name}
                              />
                            ) : (
                              <div className="h-12 w-12 rounded-lg bg-gray-200 flex items-center justify-center">
                                <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {catalogue.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {catalogue.pdf_path ? 'PDF Available' : 'No PDF'}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Description */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {catalogue.description || 'No description'}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => toggleActive(catalogue)}
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            catalogue.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {catalogue.isActive ? 'Active' : 'Inactive'}
                        </button>
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        {catalogue.pdf_path && (
                          <button
                            onClick={() => handleDownload(catalogue)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Download
                          </button>
                        )}
                        <button
                          onClick={() => openEditModal(catalogue)}
                          className="text-black hover:text-gray-700"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(catalogue)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                      No catalogues found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold mb-4">
              {editMode ? 'Edit Catalogue' : 'Add New Catalogue'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                />
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Catalogue Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                />
                {formData.imagePreview && (
                  <img
                    src={formData.imagePreview}
                    alt="Preview"
                    className="mt-2 h-20 w-20 object-cover rounded-lg"
                  />
                )}
              </div>

              {/* PDF Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  PDF File {!editMode && '*'}
                </label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handlePdfChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                  required={!editMode}
                />
                {formData.pdfPreview && (
                  <div className="mt-2 text-sm text-gray-600">
                    Selected: {formData.pdfPreview}
                  </div>
                )}
              </div>

              {/* Active Status */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-black focus:ring-black border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">
                  Active
                </label>
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-50"
                >
                  {isSubmitting ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminECatalogue;
